// Loan Compliance Module v1.0 PR 3 — the four module-status cards on the
// Legal Ops tab. Each card shows the derived state, the last check, a
// thin read-only summary of the linked operational entity, an expandable
// check history, and (gated by Compliance.RecordLegalOpsCheck) a "Record
// check" button.

import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { calendarDate, formatDate } from "../common/colorBand";
import type {
  InsurancePolicyDto,
  UccFilingDto,
} from "@/api/portfolioAdmin/types";
import type {
  LegalOpsCheckDto,
  LegalOpsCheckHistoryDto,
  LegalOpsModule,
  LegalOpsModuleStatusDto,
} from "@/api/compliance/types";
import type { LoanDetailTabId } from "@/pages/portfolioAdmin/LoanDetailPage";
import { LEGAL_OPS_MODULES, metaForModule } from "./labels";
import { ModuleStatusChip } from "./ModuleStatusChip";

interface ModuleStatusCardsProps {
  loanId: string;
  history: LegalOpsCheckHistoryDto | undefined;
  uccFilings: UccFilingDto[];
  insurancePolicies: InsurancePolicyDto[];
  canRecord: boolean;
  onRecordCheck: (module: LegalOpsModule) => void;
  onOpenLinkedTab: (tabId: LoanDetailTabId) => void;
}

function statusByModule(
  history: LegalOpsCheckHistoryDto | undefined,
): Record<LegalOpsModule, LegalOpsModuleStatusDto | undefined> {
  const map: Record<string, LegalOpsModuleStatusDto> = {};
  for (const s of history?.moduleStatuses ?? []) {
    map[s.module] = s;
  }
  return map as Record<LegalOpsModule, LegalOpsModuleStatusDto | undefined>;
}

function checksForModule(
  history: LegalOpsCheckHistoryDto | undefined,
  module: LegalOpsModule,
): LegalOpsCheckDto[] {
  return (history?.recentChecks ?? []).filter((c) => c.module === module);
}

function uccSummary(filings: UccFilingDto[]): string {
  const active = filings.filter((f) => f.currentStatus === "Active");
  if (active.length === 0) return "No active UCC filings.";
  const first = active[0];
  return `${active.length} active · ${first.filingNumber} · lapses ${calendarDate(first.lapseDate)}`;
}

function insuranceSummary(policies: InsurancePolicyDto[]): string {
  const active = policies.filter(
    (p) => p.status !== "Expired" && p.status !== "Lapsed",
  );
  if (active.length === 0) return "No active insurance policies.";
  const nextExp = [...active].sort(
    (a, b) =>
      new Date(a.expirationDate).getTime() -
      new Date(b.expirationDate).getTime(),
  )[0];
  return `${active.length} active · next expiry ${calendarDate(nextExp.expirationDate)}`;
}

export function ModuleStatusCards({
  history,
  uccFilings,
  insurancePolicies,
  canRecord,
  onRecordCheck,
  onOpenLinkedTab,
}: ModuleStatusCardsProps) {
  const statuses = useMemo(() => statusByModule(history), [history]);

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-space-3"
      data-testid="legal-ops-module-cards"
    >
      {LEGAL_OPS_MODULES.map((meta) => (
        <ModuleStatusCard
          key={meta.module}
          status={statuses[meta.module]}
          checks={checksForModule(history, meta.module)}
          uccFilings={uccFilings}
          insurancePolicies={insurancePolicies}
          canRecord={canRecord}
          onRecordCheck={() => onRecordCheck(meta.module)}
          onOpenLinkedTab={onOpenLinkedTab}
        />
      ))}
    </div>
  );
}

interface ModuleStatusCardProps {
  status: LegalOpsModuleStatusDto | undefined;
  checks: LegalOpsCheckDto[];
  uccFilings: UccFilingDto[];
  insurancePolicies: InsurancePolicyDto[];
  canRecord: boolean;
  onRecordCheck: () => void;
  onOpenLinkedTab: (tabId: LoanDetailTabId) => void;
}

function ModuleStatusCard({
  status,
  checks,
  uccFilings,
  insurancePolicies,
  canRecord,
  onRecordCheck,
  onOpenLinkedTab,
}: ModuleStatusCardProps) {
  const [expanded, setExpanded] = useState(false);

  // Status is undefined while history is loading or for an unknown module —
  // we still want to render a card so the user sees the surface.
  const module =
    status?.module ??
    (LEGAL_OPS_MODULES.find((m) => m.title.includes("Identity"))?.module as
      | LegalOpsModule
      | undefined) ??
    "LoanIdentity";
  const meta = metaForModule(module);
  const derived = status?.derivedState ?? "NotYetChecked";

  let summary = "";
  switch (module) {
    case "UccFilingMonitoring":
      summary = uccSummary(uccFilings);
      break;
    case "InsuranceRenewal":
      summary = insuranceSummary(insurancePolicies);
      break;
    case "DacaMonitoring":
      summary = "See Due Dates tab → DACA monthly review.";
      break;
    case "LoanIdentity":
      summary = "Annual loan-identity attestation.";
      break;
  }

  const latest = status?.latestCheck;
  const nextDueLabel = status?.nextDueDate
    ? `Next due ${calendarDate(status.nextDueDate)}`
    : "No upcoming cadence item.";

  return (
    <Card
      featured
      data-testid={`legal-ops-card-${module}`}
      className="flex flex-col gap-space-3"
    >
      <div className="flex items-start justify-between gap-space-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-display font-bold text-h4 m-0">{meta.title}</h3>
          <p className="text-caption text-gray-mid m-0 mt-space-1 font-mono">
            {meta.code}
          </p>
        </div>
        <ModuleStatusChip state={derived} />
      </div>

      <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
        <dt className="text-caption text-gray-mid uppercase tracking-label">
          Last check
        </dt>
        <dd
          className="m-0"
          data-testid={`legal-ops-card-${module}-last-check`}
        >
          {latest
            ? `${formatDate(latest.checkedAt)} · ${latest.outcome}`
            : "Never recorded"}
        </dd>
        <dt className="text-caption text-gray-mid uppercase tracking-label">
          Next due
        </dt>
        <dd className="m-0">{nextDueLabel}</dd>
        <dt className="text-caption text-gray-mid uppercase tracking-label">
          Summary
        </dt>
        <dd className="m-0">{summary}</dd>
      </dl>

      <div className="flex items-center justify-between gap-space-2 flex-wrap">
        <div className="flex items-center gap-space-2">
          {meta.linkedTab && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => onOpenLinkedTab(meta.linkedTab!)}
              data-testid={`legal-ops-card-${module}-link`}
            >
              Open {meta.linkedTabLabel}
            </Button>
          )}
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`legal-ops-card-${module}-history-toggle`}
          >
            {expanded ? (
              <>
                <ChevronUp size={14} aria-hidden /> Hide history
              </>
            ) : (
              <>
                <ChevronDown size={14} aria-hidden /> Check history (
                {checks.length})
              </>
            )}
          </Button>
        </div>
        {canRecord && (
          <Button
            variant="primary"
            size="sm"
            onClick={onRecordCheck}
            data-testid={`legal-ops-card-${module}-record`}
          >
            Record check
          </Button>
        )}
      </div>

      {expanded && (
        <div
          className="border-t border-border-light pt-space-2"
          data-testid={`legal-ops-card-${module}-history`}
        >
          {checks.length === 0 ? (
            <p className="text-caption text-gray-mid m-0">
              No checks recorded yet.
            </p>
          ) : (
            <ul className="flex flex-col gap-space-1 m-0 p-0 list-none">
              {checks.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-space-2 text-small"
                >
                  <span>
                    {formatDate(c.checkedAt)} · {c.periodLabel}
                  </span>
                  <span
                    className={
                      c.outcome === "Pass"
                        ? "text-status-success"
                        : "text-status-error"
                    }
                  >
                    {c.outcome}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
